import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import AdminShell, { SECTIONS } from '@/components/admin/AdminShell';
import AdminLogin from '@/components/admin/AdminLogin';
import UsersSection from '@/components/admin/UsersSection';
import { OverviewSection, AdminsSection, IpSection, SessionsSection, AuditSection } from '@/components/admin/OtherSections';
import {
  ResetPasswordDialog, AddIpDialog, EditUserDialog, AddUserDialog, AddAdminDialog,
} from '@/components/admin/AdminDialogs';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const EMPTY_USER_FORM = {
  email: '', display_name: '', login_password: '', email_password: '',
  imap_host: 'mx01.netlen.com.tr', imap_port: 993, smtp_host: 'mx01.netlen.com.tr', smtp_port: 465,
};

const readStoredAdmin = () => {
  try { return JSON.parse(localStorage.getItem('admin_user') || 'null'); } catch { return null; }
};

export const AdminPage = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Adres: /admin/<bölüm>[/<kullanıcı id>]
  const parts = location.pathname.split('/').filter(Boolean).slice(1);
  const section = SECTIONS.some((s) => s.id === parts[0]) ? parts[0] : null;
  const selectedId = section === 'users' && /^\d{1,12}$/.test(parts[1] || '') ? Number(parts[1]) : null;

  const [adminToken, setAdminToken] = useState(localStorage.getItem('admin_token'));
  const [adminUser, setAdminUser] = useState(readStoredAdmin);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [ipCheckPending, setIpCheckPending] = useState(true);
  const [ipAllowed, setIpAllowed] = useState(false);

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [health, setHealth] = useState(null);
  const [isCheckingHealth, setIsCheckingHealth] = useState(false);
  const [admins, setAdmins] = useState([]);
  const [adminIPs, setAdminIPs] = useState([]);
  const [details, setDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [resetTarget, setResetTarget] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [ipTarget, setIpTarget] = useState(null);
  const [newIPAddress, setNewIPAddress] = useState('');
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUserForm, setNewUserForm] = useState(EMPTY_USER_FORM);
  const [showAddAdmin, setShowAddAdmin] = useState(false);
  const [newAdminForm, setNewAdminForm] = useState({ username: '', password: '', role: 'admin' });
  const [showAddAdminIP, setShowAddAdminIP] = useState(false);
  const [newAdminIP, setNewAdminIP] = useState({ ip_address: '', description: '' });
  const [confirm, setConfirm] = useState(null); // { title, description, confirmLabel, destructive, onConfirm }
  const detailsRequestRef = useRef(0);

  const handleLogout = useCallback(() => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    setAdminToken(null);
    setAdminUser(null);
    setIsAuthenticated(false);
    setDetails(null);
  }, []);

  const adminRequest = useCallback(async (endpoint, options = {}, token = null) => {
    const response = await fetch(`${API_URL}/api/admin${endpoint}`, {
      ...options,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token || adminToken}`, ...options.headers },
    });
    let data = {};
    try { data = await response.json(); } catch { /* gövde yok */ }
    if (response.status === 401) {
      handleLogout();
      throw new Error(data.detail || 'Oturum süresi doldu, yeniden giriş yapın');
    }
    if (!response.ok) throw new Error(data.detail || 'Bir hata oluştu');
    return data;
  }, [adminToken, handleLogout]);

  // ---- Giriş / IP denetimi ----
  useEffect(() => {
    (async () => {
      try {
        const data = await (await fetch(`${API_URL}/api/admin/check-ip`)).json();
        if (data.allowed) { setIpAllowed(true); setIpCheckPending(false); } else { window.location.replace('/'); }
      } catch {
        setIpAllowed(true); // uç nokta yoksa erişime izin ver (eski sürümlerle uyum)
        setIpCheckPending(false);
      }
    })();
  }, []);

  // ---- Veri yükleme ----
  const fetchAllUsers = useCallback(async (search = '') => {
    const perPage = 200;
    let all = [];
    for (let page = 1, guard = 0; guard < 100; guard += 1, page += 1) {
      const data = await adminRequest(`/users?page=${page}&per_page=${perPage}${search ? `&search=${encodeURIComponent(search)}` : ''}`);
      const batch = data.users || [];
      all = all.concat(batch);
      if (all.length >= (data.total || 0) || batch.length < perPage) break;
    }
    return all;
  }, [adminRequest]);

  const checkHealth = useCallback(async () => {
    setIsCheckingHealth(true);
    try { setHealth(await adminRequest('/service-health')); } catch { setHealth(null); } finally { setIsCheckingHealth(false); }
  }, [adminRequest]);

  const refreshUsers = useCallback(async () => {
    try {
      const [statsData, allUsers] = await Promise.all([adminRequest('/stats'), fetchAllUsers(searchQuery.trim())]);
      setStats(statsData);
      setUsers(allUsers);
    } catch (e) { toast.error(e.message); }
  }, [adminRequest, fetchAllUsers, searchQuery]);

  const loadAdmins = useCallback(async () => {
    try { setAdmins((await adminRequest('/admins')).admins || []); } catch (e) { toast.error(e.message); }
  }, [adminRequest]);

  const loadAdminIPs = useCallback(async () => {
    try { setAdminIPs((await adminRequest('/allowed-ips')).ips || []); } catch (e) { toast.error(e.message); }
  }, [adminRequest]);

  const loadDetails = useCallback(async (userId) => {
    const request = ++detailsRequestRef.current;
    setDetailsLoading(true);
    try {
      const data = await adminRequest(`/users/${userId}/details`);
      if (request === detailsRequestRef.current) setDetails(data);
    } catch (e) {
      if (request === detailsRequestRef.current) { setDetails(null); toast.error(e.message); }
    } finally {
      if (request === detailsRequestRef.current) setDetailsLoading(false);
    }
  }, [adminRequest]);

  // Oturum açık olunca (ya da sayfa yenilenince) ilk yükleme
  useEffect(() => {
    if (!adminToken || !ipAllowed) return;
    setIsAuthenticated(true);
    setIsLoading(true);
    Promise.all([adminRequest('/stats'), fetchAllUsers()])
      .then(([s, u]) => { setStats(s); setUsers(u); })
      .catch((e) => toast.error(e.message))
      .finally(() => setIsLoading(false));
    checkHealth();
    loadAdmins();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminToken, ipAllowed]);

  // Bilinmeyen adresleri /admin/users'a çevir
  useEffect(() => {
    if (isAuthenticated && !section) navigate('/admin/users', { replace: true });
  }, [isAuthenticated, section, navigate]);

  useEffect(() => { if (isAuthenticated && section === 'admins') loadAdmins(); }, [isAuthenticated, section, loadAdmins]);
  useEffect(() => { if (isAuthenticated && section === 'ip') loadAdminIPs(); }, [isAuthenticated, section, loadAdminIPs]);
  useEffect(() => { if (isAuthenticated && section === 'overview') { refreshUsers(); checkHealth(); } }, [isAuthenticated, section]); // eslint-disable-line react-hooks/exhaustive-deps

  // Adresteki kullanıcı değişince ayrıntıları getir
  useEffect(() => {
    if (!isAuthenticated) return;
    if (selectedId) loadDetails(selectedId);
    else { detailsRequestRef.current += 1; setDetails(null); setDetailsLoading(false); }
  }, [isAuthenticated, selectedId, loadDetails]);

  // Sunucu tarafı arama (tüm veritabanında), yazma durunca
  const searchMountRef = useRef(false);
  useEffect(() => {
    if (!searchMountRef.current) { searchMountRef.current = true; return undefined; }
    if (!isAuthenticated) return undefined;
    const t = setTimeout(async () => {
      try { setUsers(await fetchAllUsers(searchQuery.trim())); } catch { /* sessiz */ }
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  // ---- Gezinme ----
  const goSection = (id) => navigate(`/admin/${id}`);
  const selectUser = (id) => navigate(id ? `/admin/users/${id}` : '/admin/users');
  const handleSearch = (value) => {
    setSearchQuery(value);
    if (section !== 'users') navigate('/admin/users');
  };

  // ---- Giriş ----
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setIsLoggingIn(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ username, password }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Giriş başarısız');
      localStorage.setItem('admin_token', data.access_token);
      localStorage.setItem('admin_user', JSON.stringify(data.admin || null));
      setAdminUser(data.admin || null);
      setPassword('');
      setAdminToken(data.access_token);
    } catch (error) {
      setLoginError(error.message);
    } finally {
      setIsLoggingIn(false);
    }
  };

  // ---- İşlemler ----
  const run = async (fn, okMessage) => {
    try {
      const result = await fn();
      if (okMessage) toast.success(typeof okMessage === 'function' ? okMessage(result) : okMessage);
      return true;
    } catch (e) {
      toast.error(e.message);
      return false;
    }
  };
  const submit = async (fn, okMessage) => {
    setIsSubmitting(true);
    try { return await run(fn, okMessage); } finally { setIsSubmitting(false); }
  };
  const ask = (options) => setConfirm(options);

  const reloadAfterUserChange = (userId) => {
    refreshUsers();
    if (userId && userId === selectedId) loadDetails(userId);
  };

  const userActions = {
    onEdit: (user) => {
      setEditTarget(user);
      setEditForm({
        display_name: user.display_name || '', imap_host: user.imap_host, imap_port: user.imap_port,
        smtp_host: user.smtp_host, smtp_port: user.smtp_port, bulk_mail_enabled: !!user.bulk_mail_enabled,
        bulk_mail_daily_limit: user.bulk_mail_daily_limit ?? 500, bulk_mail_hourly_limit: user.bulk_mail_hourly_limit ?? 200,
      });
    },
    onEditById: async (id) => {
      try { userActions.onEdit((await adminRequest(`/users/${id}/details`)).user); } catch (e) { toast.error(e.message); }
    },
    onReset: (user) => { setNewPassword(''); setResetTarget(user); },
    onToggleStatus: (user) => ask({
      title: user.is_active ? 'Hesap askıya alınsın mı?' : 'Hesap etkinleştirilsin mi?',
      description: user.is_active ? `${user.email} artık webmail'e giriş yapamaz. İstediğiniz zaman yeniden etkinleştirebilirsiniz.` : `${user.email} yeniden giriş yapabilecek.`,
      confirmLabel: user.is_active ? 'Askıya al' : 'Etkinleştir', destructive: user.is_active,
      onConfirm: async () => { if (await run(() => adminRequest(`/users/${user.id}/toggle-active`, { method: 'PUT' }), user.is_active ? 'Hesap askıya alındı' : 'Hesap etkinleştirildi')) reloadAfterUserChange(user.id); },
    }),
    onDelete: (user) => ask({
      title: 'Kullanıcı silinsin mi?',
      description: `${user.email} ve tüm webmail verileri (kişiler, etkinlikler, imzalar, etiketler, zamanlanmış mailler...) kalıcı olarak silinir. Posta kutusundaki mailler silinmez. Bu işlem geri alınamaz.`,
      confirmLabel: 'Kalıcı olarak sil', destructive: true,
      onConfirm: async () => { if (await run(() => adminRequest(`/users/${user.id}`, { method: 'DELETE' }), 'Kullanıcı silindi')) { if (user.id === selectedId) selectUser(null); refreshUsers(); } },
    }),
    onCpanelTest: async (user) => {
      try {
        const result = await adminRequest(`/cpanel-test?email=${encodeURIComponent(user.email)}`);
        if (!result.configured) { toast.error('cPanel yapılandırılmamış', { description: result.detail, duration: 8000 }); return; }
        const description = <div className="whitespace-pre-line text-xs">{result.steps.map((st) => `${st.ok ? '✓' : '✗'} ${st.step}${st.info ? `: ${st.info}` : ''}`).join('\n')}</div>;
        if (result.ok) toast.success('cPanel bağlantısı çalışıyor', { description, duration: 15000 });
        else toast.error('cPanel testi başarısız', { description, duration: 15000 });
      } catch (e) { toast.error(e.message); }
    },
    onDisable2FA: (user) => ask({
      title: '2FA devre dışı bırakılsın mı?', description: `${user.email} için iki faktörlü doğrulama kapatılır.`, confirmLabel: 'Devre dışı bırak', destructive: true,
      onConfirm: async () => { if (await run(() => adminRequest(`/users/${user.id}/disable-2fa`, { method: 'POST' }), '2FA devre dışı bırakıldı')) reloadAfterUserChange(user.id); },
    }),
    onToggleIP: async (user) => {
      if (await run(() => adminRequest(`/users/${user.id}/toggle-ip-restriction`, { method: 'POST' }), (r) => (r.ip_restriction_enabled ? 'IP kısıtlaması etkinleştirildi' : 'IP kısıtlaması kapatıldı'))) reloadAfterUserChange(user.id);
    },
    onAddIP: (user) => { setNewIPAddress(''); setIpTarget(user); },
    onRemoveIP: (user, ip) => ask({
      title: 'IP adresi kaldırılsın mı?', description: `${ip.ip_address} ${user.email} için izin listesinden çıkarılır.`, confirmLabel: 'Kaldır', destructive: true,
      onConfirm: async () => { if (await run(() => adminRequest(`/users/${user.id}/remove-ip/${ip.id}`, { method: 'DELETE' }), 'IP adresi kaldırıldı')) loadDetails(user.id); },
    }),
    onInvalidateSession: (user, session) => ask({
      title: 'Oturum sonlandırılsın mı?', description: `${session.ip_address} adresindeki oturum kapatılır.`, confirmLabel: 'Sonlandır', destructive: true,
      onConfirm: async () => { if (await run(() => adminRequest(`/users/${user.id}/sessions/${session.id}`, { method: 'DELETE' }), 'Oturum sonlandırıldı')) reloadAfterUserChange(user.id); },
    }),
    onInvalidateAll: (user) => ask({
      title: 'Tüm oturumlar sonlandırılsın mı?', description: `${user.email} tüm cihazlarda oturumdan çıkarılır.`, confirmLabel: 'Hepsini sonlandır', destructive: true,
      onConfirm: async () => { if (await run(() => adminRequest(`/users/${user.id}/sessions`, { method: 'DELETE' }), 'Tüm oturumlar sonlandırıldı')) reloadAfterUserChange(user.id); },
    }),
  };

  const submitReset = async () => {
    if (newPassword.length < 6) return toast.error('Şifre en az 6 karakter olmalıdır');
    if (await submit(() => adminRequest(`/users/${resetTarget.id}/reset-password`, { method: 'PUT', body: JSON.stringify({ new_password: newPassword }) }), 'Şifre sıfırlandı')) setResetTarget(null);
    return undefined;
  };
  const submitUserIP = async () => {
    if (!newIPAddress.trim()) return toast.error('IP adresi girin');
    if (await submit(() => adminRequest(`/users/${ipTarget.id}/add-ip`, { method: 'POST', body: JSON.stringify({ ip_address: newIPAddress.trim() }) }), 'IP adresi eklendi')) {
      const id = ipTarget.id; setIpTarget(null); loadDetails(id);
    }
    return undefined;
  };
  const submitEdit = async () => {
    if (await submit(() => adminRequest(`/users/${editTarget.id}`, { method: 'PUT', body: JSON.stringify(editForm) }), 'Kullanıcı güncellendi')) {
      const id = editTarget.id; setEditTarget(null); reloadAfterUserChange(id);
    }
  };
  const submitNewUser = async () => {
    const f = newUserForm;
    if (!f.email || !f.login_password || !f.email_password || !f.imap_host || !f.smtp_host) return toast.error('E-posta, giriş şifresi, posta kutusu şifresi ve IMAP/SMTP sunucusu zorunludur');
    if (f.login_password.length < 8) return toast.error('Giriş şifresi en az 8 karakter olmalıdır');
    if (await submit(() => adminRequest('/users', { method: 'POST', body: JSON.stringify({ ...f, imap_port: parseInt(f.imap_port, 10) || 993, smtp_port: parseInt(f.smtp_port, 10) || 465 }) }), 'Kullanıcı oluşturuldu')) {
      setShowAddUser(false); setNewUserForm(EMPTY_USER_FORM); refreshUsers();
    }
    return undefined;
  };
  const submitNewAdmin = async () => {
    if (!newAdminForm.username || !newAdminForm.password) return toast.error('Kullanıcı adı ve şifre zorunludur');
    if (newAdminForm.password.length < 6) return toast.error('Şifre en az 6 karakter olmalıdır');
    if (await submit(() => adminRequest('/admins', { method: 'POST', body: JSON.stringify(newAdminForm) }), 'Yeni admin oluşturuldu')) {
      setShowAddAdmin(false); setNewAdminForm({ username: '', password: '', role: 'admin' }); loadAdmins();
    }
    return undefined;
  };
  const submitAdminIP = async () => {
    if (!newAdminIP.ip_address.trim()) return toast.error('IP adresi girin');
    if (await submit(() => adminRequest('/allowed-ips', { method: 'POST', body: JSON.stringify(newAdminIP) }), 'Admin IP adresi eklendi')) {
      setShowAddAdminIP(false); setNewAdminIP({ ip_address: '', description: '' }); loadAdminIPs();
    }
    return undefined;
  };

  // ---- Görünüm ----
  if (ipCheckPending || !ipAllowed) {
    return <div className="min-h-screen bg-background flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>;
  }
  if (!isAuthenticated) {
    return <AdminLogin username={username} password={password} onUsername={setUsername} onPassword={setPassword} error={loginError} loading={isLoggingIn} onSubmit={handleLogin} />;
  }

  return (
    <>
      <AdminShell adminUser={adminUser} section={section || 'users'} onSection={goSection} userCount={stats?.total_users} searchQuery={searchQuery} onSearch={handleSearch} onLogout={handleLogout}>
        {(!section || section === 'users') && (
          <UsersSection
            users={users} stats={stats} health={health} isCheckingHealth={isCheckingHealth} onRefreshHealth={checkHealth} isLoading={isLoading}
            searchQuery={searchQuery} onSearch={setSearchQuery} selectedId={selectedId} onSelect={selectUser}
            details={details} detailsLoading={detailsLoading} onAddUser={() => setShowAddUser(true)} actions={userActions}
          />
        )}
        {section === 'overview' && <OverviewSection stats={stats} health={health} onRefreshHealth={checkHealth} isCheckingHealth={isCheckingHealth} request={adminRequest} onOpenLogs={() => goSection('logs')} />}
        {section === 'admins' && (
          <AdminsSection
            admins={admins} onAdd={() => setShowAddAdmin(true)}
            onToggle={async (a) => { if (await run(() => adminRequest(`/admins/${a.id}/toggle-status`, { method: 'POST' }), (r) => (r.is_active ? 'Admin etkinleştirildi' : 'Admin devre dışı bırakıldı'))) loadAdmins(); }}
            onDelete={(a) => ask({ title: 'Admin silinsin mi?', description: `${a.username} yönetim paneline erişemez.`, confirmLabel: 'Sil', destructive: true, onConfirm: async () => { if (await run(() => adminRequest(`/admins/${a.id}`, { method: 'DELETE' }), 'Admin silindi')) loadAdmins(); } })}
          />
        )}
        {section === 'ip' && (
          <IpSection
            ips={adminIPs} isSubmitting={isSubmitting} onAdd={() => setShowAddAdminIP(true)}
            onAddCurrent={async () => { if (await submit(async () => { const r = await adminRequest('/allowed-ips/add-current', { method: 'POST' }); toast.success(r.message); }, null)) loadAdminIPs(); }}
            onRemove={(ip) => ask({ title: 'IP adresi kaldırılsın mı?', description: `${ip.ip_address} artık yönetim paneline erişemez.`, confirmLabel: 'Kaldır', destructive: true, onConfirm: async () => { if (await run(() => adminRequest(`/allowed-ips/${ip.id}`, { method: 'DELETE' }), 'IP adresi kaldırıldı')) loadAdminIPs(); } })}
          />
        )}
        {section === 'sessions' && <SessionsSection request={adminRequest} onChanged={refreshUsers} />}
        {section === 'logs' && <AuditSection request={adminRequest} />}
      </AdminShell>

      <ResetPasswordDialog open={Boolean(resetTarget)} onOpenChange={(o) => !o && setResetTarget(null)} user={resetTarget} value={newPassword} onChange={setNewPassword} onSubmit={submitReset} submitting={isSubmitting} />
      <AddIpDialog open={Boolean(ipTarget)} onOpenChange={(o) => !o && setIpTarget(null)} title="IP adresi ekle" description={ipTarget ? `${ipTarget.email} için izin verilen IP.` : ''} value={newIPAddress} onChange={setNewIPAddress} onSubmit={submitUserIP} submitting={isSubmitting} />
      <AddIpDialog open={showAddAdminIP} onOpenChange={setShowAddAdminIP} title="Admin IP adresi ekle" description="Yönetim paneline erişebilecek IP." value={newAdminIP.ip_address} onChange={(v) => setNewAdminIP({ ...newAdminIP, ip_address: v })} note={newAdminIP.description} onNote={(v) => setNewAdminIP({ ...newAdminIP, description: v })} onSubmit={submitAdminIP} submitting={isSubmitting} testId="add-admin-ip-dialog" />
      <EditUserDialog open={Boolean(editTarget)} onOpenChange={(o) => !o && setEditTarget(null)} form={editForm} setForm={setEditForm} onSubmit={submitEdit} submitting={isSubmitting} />
      <AddUserDialog open={showAddUser} onOpenChange={setShowAddUser} form={newUserForm} setForm={setNewUserForm} onSubmit={submitNewUser} submitting={isSubmitting} />
      <AddAdminDialog open={showAddAdmin} onOpenChange={setShowAddAdmin} form={newAdminForm} setForm={setNewAdminForm} onSubmit={submitNewAdmin} submitting={isSubmitting} />

      <AlertDialog open={Boolean(confirm)} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent data-testid="confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{confirm?.title}</AlertDialogTitle>
            <AlertDialogDescription>{confirm?.description}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Vazgeç</AlertDialogCancel>
            <AlertDialogAction
              className={confirm?.destructive ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90' : ''}
              onClick={() => { const fn = confirm?.onConfirm; setConfirm(null); fn?.(); }}
              data-testid="confirm-submit"
            >
              {confirm?.confirmLabel || 'Onayla'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default AdminPage;
